// Compact FAQ accordion — the 4 launch-required questions.
import { useState } from "react";

export type FaqContent = { question: string; answer: string };

export const HOMEPAGE_FAQS: FaqContent[] = [
  {
    question: "What is Companion?",
    answer:
      "Companion is your always-present AI assistant inside Scribenote. Simply chat with it to get it to help summarizing cases, drafting discharge emails, looking up dosages, and more.",
  },
  {
    question: "What are Treats?",
    answer:
      "Treats are what Companion runs on. Every task takes a bite, and bigger jobs take bigger bites.",
  },
  {
    question: "How much does it cost?",
    answer:
      'Each new Companion sign-up gets 500 free Treats. After that you\'ll be able to purchase more Treats. Learn more about Companion\'s pricing here <a class="font-semibold text-indigo-600 underline" href="/pricing">askcompanion.com/pricing</a>.',
  },
  {
    question: "Can I trust Companion with my patient/client data?",
    answer:
      'Yes! Scribenote — the company behind Companion — holds SOC 2 Type II attestation for how we handle and protect your data, and our AI management system is certified to ISO/IEC 42001 by Insight Assurance, the international standard for how a company builds and governs AI responsibly. To learn more about our security standards, visit <a class="font-semibold text-indigo-600 underline" href="https://trust.scribenote.com" target="_blank" rel="noopener noreferrer">trust.scribenote.com</a>.',
  },
];

export function FAQ() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="px-[clamp(24px,5vw,80px)] pb-24">
      <div className="mx-auto max-w-[820px]">
        <h2 className="font-companion tracking-[-0.01em] mt-0 mb-10 text-center text-[clamp(28px,3.4vw,40px)] font-bold tracking-[-0.025em] text-primary">
          Quick questions.
        </h2>
        <div className="flex flex-col gap-2.5">
          {HOMEPAGE_FAQS.map((f, i) => {
            const isOpen = open === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;
            return (
              <div
                key={i}
                className={`rounded-2xl border border-grindigo-100 bg-white font-body transition-[box-shadow] duration-200 ${
                  isOpen
                    ? "shadow-[0_8px_24px_-10px_rgba(99,102,241,0.2)]"
                    : "shadow-[0_2px_8px_-4px_rgba(99,102,241,0.08)]"
                }`}
              >
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full cursor-pointer items-center justify-between gap-4 rounded-2xl border-0 bg-transparent px-6 py-5 text-left font-body"
                >
                  <span className="text-base font-semibold text-primary">
                    {f.question}
                  </span>
                  <span
                    className={`grid h-7 w-7 shrink-0 place-items-center rounded-full bg-indigo-50 text-indigo-700 transition-transform duration-200 ${
                      isOpen ? "rotate-45" : ""
                    }`}
                    aria-hidden="true"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 5v14M5 12h14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {isOpen && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="px-6 pb-5 text-[15px] leading-[1.6] text-secondary"
                    dangerouslySetInnerHTML={{ __html: f.answer }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
